import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { TransactionType } from "@/models/point-transaction";

export const TRANSACTION_TYPE_VALUES: TransactionType[] = [
  "purchase",
  "bonus",
  "manual_adjustment",
  "refund",
  "signup_bonus",
  "redemption",
  "expiration",
  "direct_spending",
  "reversal",
];

export const TRANSACTION_TYPE_GROUPED_VALUES = [
  {
    groupKey: "earnings",
    options: ["purchase", "bonus", "manual_adjustment", "refund", "signup_bonus"] as TransactionType[],
  },
  {
    groupKey: "spendings",
    options: ["expiration", "direct_spending", "reversal"] as TransactionType[],
  },
];

interface TransactionTypeSelectProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  includeAllOption?: boolean;
  grouped?: boolean;
  className?: string;
}

export function TransactionTypeSelect({
  value,
  onChange,
  placeholder,
  disabled,
  includeAllOption = false,
  grouped = false,
  className,
}: TransactionTypeSelectProps) {
  const { t } = useTranslation();

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className={cn("w-full", className)}>
        <SelectValue placeholder={placeholder ?? t("fields.select_type")} />
      </SelectTrigger>
      <SelectContent>
        {includeAllOption && <SelectItem value="all">{t("fields.all_types")}</SelectItem>}
        {grouped ? (
          TRANSACTION_TYPE_GROUPED_VALUES.map((group) => (
            <SelectGroup key={group.groupKey}>
              <SelectLabel>{t(`transaction_types.groups.${group.groupKey}`)}</SelectLabel>
              {group.options.map((optionValue) => (
                <SelectItem key={optionValue} value={optionValue}>
                  {t(`transaction_types.${optionValue}`)}
                </SelectItem>
              ))}
            </SelectGroup>
          ))
        ) : (
          TRANSACTION_TYPE_VALUES.map((optionValue) => (
            <SelectItem key={optionValue} value={optionValue}>
              {t(`transaction_types.${optionValue}`)}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
}
